export type TicketStatus = 'open' | 'pending' | 'closed';
export type TicketPriority = 'Low' | 'Normal' | 'High';
export type AttachmentKind = 'video' | 'image' | 'file';
export type QueryFilter = 'all' | TicketStatus;
export type QuerySort = 'newest' | 'oldest';

export interface TicketAttachment {
  id: string;
  name: string;
  sizeLabel: string;
  kind: AttachmentKind;
}

export interface TicketMessage {
  id: string;
  author: 'user' | 'support' | 'system';
  body: string;
  atLabel: string;
  attachments?: TicketAttachment[];
}

export interface SupportTicket {
  id: string;
  code: string;
  subject: string;
  preview: string;
  status: TicketStatus;
  category: string;
  priority: TicketPriority;
  createdAt: string;
  createdOnLabel: string;
  createdExact: string;
  listTimeLabel: string;
  lastResponseLabel: string;
  deviceName: string;
  deviceDetail: string;
  whatsapp?: string;
  messages: TicketMessage[];
}

export interface TicketStats {
  all: number;
  awaiting: number;
  progress: number;
  resolved: number;
}

/** Sample handset shown on a new query until the support API sends the real device. */
export const SAMPLE_DEVICE = {
  name: 'samsung SM-E426B',
  detail: 'Android 13 · Mock Loc v1.15',
};

export const COUNTRY_CODES = [
  { dial: '+91', flag: '🇮🇳', name: 'India' },
  { dial: '+1', flag: '🇺🇸', name: 'United States' },
  { dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { dial: '+61', flag: '🇦🇺', name: 'Australia' },
  { dial: '+971', flag: '🇦🇪', name: 'United Arab Emirates' },
  { dial: '+65', flag: '🇸🇬', name: 'Singapore' },
] as const;

const recording: TicketAttachment = {
  id: 'a-rec',
  name: 'screen_recording.mp4',
  sizeLabel: '12.4 MB',
  kind: 'video',
};

const settingsShot: TicketAttachment = {
  id: 'a-shot',
  name: 'itinerary_settings.png',
  sizeLabel: '1.2 MB',
  kind: 'image',
};

const driveShot: TicketAttachment = {
  id: 'a-drive',
  name: 'drive_error.png',
  sizeLabel: '640 KB',
  kind: 'image',
};

function message(
  id: string,
  author: TicketMessage['author'],
  body: string,
  atLabel: string,
  attachments?: TicketAttachment[]
): TicketMessage {
  return { id, author, body, atLabel, attachments };
}

export const HELP_TICKETS: SupportTicket[] = [
  {
    id: 'ML-2026-4EA19',
    code: 'ML-2026-4EA19',
    subject: 'Itinerary stops not updating',
    preview: 'My itinerary stops are not updating when I start the mock location session...',
    status: 'pending',
    category: 'App functionality',
    priority: 'Normal',
    createdAt: '2026-10-24T11:22:00.000Z',
    createdOnLabel: 'Oct 24, 2026',
    createdExact: 'Oct 24, 2026, 4:52 PM',
    listTimeLabel: 'Today, 4:52 PM',
    lastResponseLabel: '18 min ago',
    deviceName: 'samsung SM-E426B',
    deviceDetail: 'Android 13 · Mock Loc v1.15',
    messages: [
      message('m1', 'system', 'You opened this ticket · Oct 24, 2026, 4:52 PM', ''),
      message(
        'm2',
        'user',
        'My itinerary stops are not updating when I start the mock location session.',
        '4:52 PM',
        [recording]
      ),
      message(
        'm3',
        'support',
        "Thanks for the details. Could you share your app version and a screenshot of the itinerary settings? We'll take a look.",
        '5:10 PM'
      ),
      message(
        'm4',
        'user',
        "I'm using v1.1.15. I've attached the settings screenshot.",
        '5:18 PM',
        [settingsShot]
      ),
    ],
  },
  {
    id: 'ML-2026-457B7',
    code: 'ML-2026-457B7',
    subject: 'Location pointer jumps after pause',
    preview: 'After pausing the session, the location pointer jumps to a different place...',
    status: 'open',
    category: 'Playback',
    priority: 'Normal',
    createdAt: '2026-10-01T05:50:00.000Z',
    createdOnLabel: 'Oct 1, 2026',
    createdExact: 'Oct 1, 2026, 11:20 AM',
    listTimeLabel: 'Oct 1, 2026',
    lastResponseLabel: '2 hr ago',
    deviceName: 'Pixel 8',
    deviceDetail: 'Android 14 · Mock Loc v1.15',
    messages: [
      message('m5', 'system', 'You opened this ticket · Oct 1, 2026, 11:20 AM', ''),
      message(
        'm6',
        'user',
        'After pausing the session, the location pointer jumps to a different place. It happens on the stationary player and on a playlist.',
        '11:20 AM'
      ),
      message(
        'm7',
        'support',
        'Thanks, we can reproduce a jump after pause. A fix is in progress for the next release.',
        '1:05 PM'
      ),
    ],
  },
  {
    id: 'ML-2026-9C21F',
    code: 'ML-2026-9C21F',
    subject: 'Playlist will not resume after the phone locks',
    preview: 'The playlist stops when the screen locks and does not continue...',
    status: 'pending',
    category: 'Playback',
    priority: 'Normal',
    createdAt: '2026-09-18T09:15:00.000Z',
    createdOnLabel: 'Sep 18, 2026',
    createdExact: 'Sep 18, 2026, 2:45 PM',
    listTimeLabel: 'Sep 18, 2026',
    lastResponseLabel: 'Yesterday',
    deviceName: 'samsung SM-E426B',
    deviceDetail: 'Android 13 · Mock Loc v1.14',
    messages: [
      message('m8', 'system', 'You opened this ticket · Sep 18, 2026, 2:45 PM', ''),
      message(
        'm9',
        'user',
        'The playlist stops when the screen locks and does not continue. Battery optimization is off.',
        '2:45 PM'
      ),
      message(
        'm10',
        'support',
        'Please try the latest build and leave the player notification pinned. We are waiting on a screen recording if it still stops.',
        '4:10 PM'
      ),
    ],
  },
  {
    id: 'ML-2026-11AB2',
    code: 'ML-2026-11AB2',
    subject: 'Joystick speed feels slower on the web',
    preview: 'The same joystick session is slower in the browser than on the phone...',
    status: 'open',
    category: 'Joystick',
    priority: 'Normal',
    createdAt: '2026-09-02T14:40:00.000Z',
    createdOnLabel: 'Sep 2, 2026',
    createdExact: 'Sep 2, 2026, 8:10 PM',
    listTimeLabel: 'Sep 2, 2026',
    lastResponseLabel: '3 days ago',
    deviceName: 'Chrome on macOS',
    deviceDetail: 'Web · Mock Loc web',
    messages: [
      message('m11', 'system', 'You opened this ticket · Sep 2, 2026, 8:10 PM', ''),
      message(
        'm12',
        'user',
        'The same joystick session is slower in the browser than on the phone. Speed is set to 1x in both places.',
        '8:10 PM'
      ),
      message(
        'm13',
        'support',
        'Web playback is still catching up with the phone. We logged this against the joystick player.',
        '9:02 PM'
      ),
    ],
  },
  {
    id: 'ML-2026-77DE0',
    code: 'ML-2026-77DE0',
    subject: 'Stationary point missing after sync',
    preview: 'Home disappeared from the phone after a sync from the web...',
    status: 'open',
    category: 'Sync',
    priority: 'High',
    createdAt: '2026-08-14T04:05:00.000Z',
    createdOnLabel: 'Aug 14, 2026',
    createdExact: 'Aug 14, 2026, 9:35 AM',
    listTimeLabel: 'Aug 14, 2026',
    lastResponseLabel: '1 week ago',
    deviceName: 'Pixel 7',
    deviceDetail: 'Android 14 · Mock Loc v1.14',
    messages: [
      message('m14', 'system', 'You opened this ticket · Aug 14, 2026, 9:35 AM', ''),
      message(
        'm15',
        'user',
        'Home disappeared from the phone after a sync from the web. The point is still listed on the website.',
        '9:35 AM'
      ),
      message(
        'm16',
        'support',
        'We are checking the sync job for that account. Please avoid deleting the web copy until we confirm.',
        '11:20 AM'
      ),
    ],
  },
  {
    id: 'ML-2026-A8481',
    code: 'ML-2026-A8481',
    subject: 'Need help restoring backup',
    preview: 'I am unable to restore my backup from Google Drive. It shows an error...',
    status: 'closed',
    category: 'Backup',
    priority: 'Normal',
    createdAt: '2026-07-31T10:12:00.000Z',
    createdOnLabel: 'Jul 31, 2026',
    createdExact: 'Jul 31, 2026, 3:42 PM',
    listTimeLabel: 'Jul 31, 2026',
    lastResponseLabel: 'Jul 31, 2026',
    deviceName: 'samsung SM-E426B',
    deviceDetail: 'Android 13 · Mock Loc v1.13',
    messages: [
      message('m17', 'system', 'You opened this ticket · Jul 31, 2026, 3:42 PM', ''),
      message(
        'm18',
        'user',
        'I am unable to restore my backup from Google Drive. It shows an error after the file picker.',
        '3:42 PM',
        [driveShot]
      ),
      message(
        'm19',
        'support',
        'The backup file was from an older export. We restored it on our side. Pull to refresh on the Backup screen.',
        '4:30 PM'
      ),
      message('m20', 'user', 'It worked. Thank you.', '5:05 PM'),
    ],
  },
  {
    id: 'ML-2026-3B90C',
    code: 'ML-2026-3B90C',
    subject: "Can't sign in on a second phone",
    preview: 'The second phone says the device limit is already full...',
    status: 'closed',
    category: 'Account',
    priority: 'Normal',
    createdAt: '2026-06-12T08:30:00.000Z',
    createdOnLabel: 'Jun 12, 2026',
    createdExact: 'Jun 12, 2026, 2:00 PM',
    listTimeLabel: 'Jun 12, 2026',
    lastResponseLabel: 'Jun 12, 2026',
    deviceName: 'Redmi Note 12',
    deviceDetail: 'Android 13 · Mock Loc v1.12',
    messages: [
      message('m21', 'system', 'You opened this ticket · Jun 12, 2026, 2:00 PM', ''),
      message('m22', 'user', 'The second phone says the device limit is already full. I only use one handset.', '2:00 PM'),
      message(
        'm23',
        'support',
        'An old browser session was still signed in. We removed it. You can sign in on the phone again.',
        '3:15 PM'
      ),
    ],
  },
  {
    id: 'ML-2026-6154A',
    code: 'ML-2026-6154A',
    subject: 'GPS accuracy setting question',
    preview: 'What is the recommended GPS accuracy value for best results?',
    status: 'closed',
    category: 'Settings',
    priority: 'Low',
    createdAt: '2026-05-31T06:45:00.000Z',
    createdOnLabel: 'May 31, 2026',
    createdExact: 'May 31, 2026, 12:15 PM',
    listTimeLabel: 'May 31, 2026',
    lastResponseLabel: 'May 31, 2026',
    deviceName: 'samsung SM-E426B',
    deviceDetail: 'Android 12 · Mock Loc v1.11',
    messages: [
      message('m24', 'system', 'You opened this ticket · May 31, 2026, 12:15 PM', ''),
      message('m25', 'user', 'What is the recommended GPS accuracy value for best results?', '12:15 PM'),
      message(
        'm26',
        'support',
        'Use the default accuracy. A tighter value can make the pointer jitter between updates.',
        '1:40 PM'
      ),
    ],
  },
  {
    id: 'ML-2026-88F12',
    code: 'ML-2026-88F12',
    subject: 'Coins did not update after purchase',
    preview: 'The purchase succeeded but the coin balance stayed the same...',
    status: 'closed',
    category: 'Mock Coins',
    priority: 'Normal',
    createdAt: '2026-04-03T12:10:00.000Z',
    createdOnLabel: 'Apr 3, 2026',
    createdExact: 'Apr 3, 2026, 5:40 PM',
    listTimeLabel: 'Apr 3, 2026',
    lastResponseLabel: 'Apr 3, 2026',
    deviceName: 'Pixel 6a',
    deviceDetail: 'Android 14 · Mock Loc v1.10',
    whatsapp: '+91 9876543210',
    messages: [
      message('m27', 'system', 'You opened this ticket · Apr 3, 2026, 5:40 PM', ''),
      message('m28', 'user', 'The purchase succeeded but the coin balance stayed the same.', '5:40 PM'),
      message('m29', 'support', 'The receipt reached us late. The coins are on the account now.', '6:25 PM'),
    ],
  },
  {
    id: 'ML-2026-2AA61',
    code: 'ML-2026-2AA61',
    subject: 'Map stays blank on an itinerary',
    preview: 'The itinerary map is a blank gray area until I leave and come back...',
    status: 'closed',
    category: 'Map',
    priority: 'Normal',
    createdAt: '2026-03-19T03:20:00.000Z',
    createdOnLabel: 'Mar 19, 2026',
    createdExact: 'Mar 19, 2026, 8:50 AM',
    listTimeLabel: 'Mar 19, 2026',
    lastResponseLabel: 'Mar 19, 2026',
    deviceName: 'Moto G54',
    deviceDetail: 'Android 13 · Mock Loc v1.10',
    messages: [
      message('m30', 'system', 'You opened this ticket · Mar 19, 2026, 8:50 AM', ''),
      message('m31', 'user', 'The itinerary map is a blank gray area until I leave and come back.', '8:50 AM'),
      message('m32', 'support', 'This was a map-load bug in 1.10. Update to 1.11 and open the route again.', '10:05 AM'),
    ],
  },
  {
    id: 'ML-2026-C4419',
    code: 'ML-2026-C4419',
    subject: 'Dark theme text is hard to read',
    preview: 'Hint text on the player is too faint when dark theme is on...',
    status: 'closed',
    category: 'Display',
    priority: 'Low',
    createdAt: '2026-02-08T15:00:00.000Z',
    createdOnLabel: 'Feb 8, 2026',
    createdExact: 'Feb 8, 2026, 8:30 PM',
    listTimeLabel: 'Feb 8, 2026',
    lastResponseLabel: 'Feb 8, 2026',
    deviceName: 'samsung SM-A546B',
    deviceDetail: 'Android 14 · Mock Loc v1.09',
    messages: [
      message('m33', 'system', 'You opened this ticket · Feb 8, 2026, 8:30 PM', ''),
      message('m34', 'user', 'Hint text on the player is too faint when dark theme is on.', '8:30 PM'),
      message('m35', 'support', 'We raised the contrast of hint text in the next build. Thanks for the screenshot.', '9:10 PM'),
    ],
  },
  {
    id: 'ML-2026-0E773',
    code: 'ML-2026-0E773',
    subject: 'How do I share a route link',
    preview: 'I want to send an itinerary link to another phone on this account...',
    status: 'closed',
    category: 'Sharing',
    priority: 'Normal',
    createdAt: '2026-01-22T07:25:00.000Z',
    createdOnLabel: 'Jan 22, 2026',
    createdExact: 'Jan 22, 2026, 12:55 PM',
    listTimeLabel: 'Jan 22, 2026',
    lastResponseLabel: 'Jan 22, 2026',
    deviceName: 'Chrome on macOS',
    deviceDetail: 'Web · Mock Loc web',
    messages: [
      message('m36', 'system', 'You opened this ticket · Jan 22, 2026, 12:55 PM', ''),
      message('m37', 'user', 'I want to send an itinerary link to another phone on this account.', '12:55 PM'),
      message(
        'm38',
        'support',
        'Open the route, then use Share. The other phone must be signed in to the same account.',
        '1:20 PM'
      ),
    ],
  },
];

export function cloneTickets(source: SupportTicket[]): SupportTicket[] {
  return source.map((ticket) => ({
    ...ticket,
    messages: ticket.messages.map((entry) => ({
      ...entry,
      attachments: entry.attachments?.map((file) => ({ ...file })),
    })),
  }));
}

export function attachmentCount(ticket: SupportTicket): number {
  return ticket.messages.reduce((sum, entry) => sum + (entry.attachments?.length ?? 0), 0);
}

export function ticketStats(tickets: SupportTicket[]): TicketStats {
  return {
    all: tickets.length,
    awaiting: tickets.filter((ticket) => ticket.status === 'pending').length,
    progress: tickets.filter((ticket) => ticket.status === 'open').length,
    resolved: tickets.filter((ticket) => ticket.status === 'closed').length,
  };
}

export function filterTickets(tickets: SupportTicket[], filter: QueryFilter, query: string): SupportTicket[] {
  const needle = query.trim().toLowerCase();
  return tickets.filter((ticket) => {
    if (filter !== 'all' && ticket.status !== filter) return false;
    if (!needle) return true;
    const blob = `${ticket.code} ${ticket.subject} ${ticket.preview} ${ticket.category}`.toLowerCase();
    return blob.includes(needle);
  });
}

export function sortTickets(tickets: SupportTicket[], sort: QuerySort): SupportTicket[] {
  const copy = [...tickets];
  copy.sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));
  if (sort === 'newest') copy.reverse();
  return copy;
}
